import { useMemo } from 'react';
import { useUser } from '@aegix/auth-identity';
import { usePermissions } from '@aegix/auth-permissions';
import { useAllPermissions } from './queries/usePermissions';
import {
  buildPermissionCatalog,
  effectiveCodenames,
  isSubset,
  LEVELS,
} from '../utils/permission-levels';
import type { PermissionLevel } from '../types/role';

export const VISITOR_MANAGEMENT_CATEGORY = 'visitor_management';

export interface VisitorPermissionsResult {
  level: PermissionLevel;
  canObserve: boolean;
  canParticipate: boolean;
  canManage: boolean;
  isLoading: boolean;
}

export interface UseVisitorPermissionsOptions {
  enabled?: boolean;
}

/**
 * Hook evaluating ADR-048 permission levels for the `visitor_management` category.
 *
 * Feature flag (`FeatureCode.VISITOR_MANAGEMENT`) acts as the outer gate.
 * Tiered permissions (`observe`, `participate`, `manage`) act as the inner gate.
 */
export function useVisitorPermissions(
  options?: UseVisitorPermissionsOptions,
): VisitorPermissionsResult {
  const enabled = options?.enabled ?? true;
  const user = useUser();
  const userPermissions = usePermissions();
  const { data: catalogResponse, isLoading: isCatalogLoading } = useAllPermissions({ enabled });

  const userPermissionSet = useMemo(() => {
    return new Set(userPermissions ?? []);
  }, [userPermissions]);

  const catalog = useMemo(() => {
    if (!catalogResponse) return null;
    return buildPermissionCatalog(
      catalogResponse.results ?? [],
      catalogResponse.categories ?? [],
      catalogResponse.capabilities ?? [],
    );
  }, [catalogResponse]);

  const dottedByCodename = useMemo(() => {
    const map = new Map<string, string>();
    for (const perm of catalogResponse?.results ?? []) {
      if (perm.codename && perm.app_label) {
        map.set(perm.codename, `${perm.app_label}.${perm.codename}`);
      }
    }
    return map;
  }, [catalogResponse]);

  const level = useMemo<PermissionLevel>(() => {
    if (!enabled || !catalog || !user) return 'none';

    // Candidate tiers highest to lowest: manage, participate, observe
    const candidates: PermissionLevel[] = ['manage', 'participate', 'observe'];
    for (const candidate of candidates) {
      const required = effectiveCodenames(catalog, VISITOR_MANAGEMENT_CATEGORY, candidate);
      if (required.size === 0) continue;

      const dotted = new Set<string>();
      for (const codename of required) {
        const key = dottedByCodename.get(codename);
        if (key) dotted.add(key);
      }

      if (dotted.size === required.size && isSubset(dotted, userPermissionSet)) {
        return candidate;
      }
    }

    return 'none';
  }, [enabled, catalog, user, userPermissionSet, dottedByCodename]);

  const levelIndex = LEVELS.indexOf(level);
  const isUserLoading = !user;

  return {
    level,
    canObserve: levelIndex >= LEVELS.indexOf('observe'),
    canParticipate: levelIndex >= LEVELS.indexOf('participate'),
    canManage: levelIndex >= LEVELS.indexOf('manage'),
    isLoading: enabled ? (isCatalogLoading || isUserLoading) : false,
  };
}
